import React, { useEffect, useState, useMemo, useCallback } from "react";
import { icons } from "../../../Assets/Icons/icons";
import { createPortal } from "react-dom";
import AddNewModal from "./AddNewModal";
import AddOneButton from "../../common/AddOneButton";
import AdminCategorySkeleton from "./AdminCategorySkeleton";
import DeleteAlertModal from "../../common/DeleteAlertModal";
import axios from "axios";
import UpdateModal from "./UpdateModal";
import CategoryDetailsModal from "./CategoryDetailsModal";
import { BaggageClaim, Search } from "lucide-react";

const AdminCategoryHome = ({
  hideHeader = false,
  onSelectSubcategoriesOf,
  onCategoryCountChange,
}) => {
  const [isAddModal, setIsAddModal] = useState(false);
  const [entriesPerPage] = useState(8);
  const [currentPage, setCurrentPage] = useState(1);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState("");
  const [categoryData, setCategoryData] = useState([]);
  const [error, setError] = useState(null);
  const [isUpdateModal, setIsUpdateModal] = useState(false);
  const [categoryId, setCategoryId] = useState();
  const [isDetailsModal, setIsDetailsModal] = useState(false);
  const [selectedCategory, setSelectedCategory] = useState(null);
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [categoryToDelete, setCategoryToDelete] = useState(null);
  const [deleteLoading, setDeleteLoading] = useState(false);

  // Helper function to get English name
  const getEnglishName = (category) => {
    if (!category || !category.name) return "";
    
    // If name is a string (for backward compatibility)
    if (typeof category.name === 'string') {
      return category.name;
    }
    
    // If name is an object, get English name
    if (typeof category.name === 'object') {
      return category.name.en || category.name.english || 
             Object.values(category.name)[0] || 
             "Unnamed Category";
    }
    
    return "Category";
  };

  // Helper function to get English description
  const getEnglishDescription = (category) => {
    if (!category || !category.description) return "No description available.";
    
    // If description is a string
    if (typeof category.description === 'string') {
      return category.description;
    }
    
    // If description is an object, get English description
    if (typeof category.description === 'object') {
      return category.description.en || category.description.english || 
             Object.values(category.description)[0] || 
             "";
    }
    
    return "";
  };

  // Fetch all categories
  const fetchCategories = useCallback(async () => {
    setLoading(true);
    try {
      const response = await axios.get(
        `${process.env.REACT_APP_API_URL}/categories/get-all-categories`
      );
      const list = response.data || [];
      setCategoryData(list);
      if (onCategoryCountChange) onCategoryCountChange(list.length);
    } catch (error) {
      console.error("Error fetching categories:", error);
      setError("Failed to load categories. Please try again.");
    } finally {
      setLoading(false);
    }
  }, [onCategoryCountChange]);

  const handleSuccess = async () => {
    await fetchCategories();
  };

  useEffect(() => {
    fetchCategories();
  }, [fetchCategories]);

  const handleDetailsModal = (category) => {
    setSelectedCategory(category);
    setIsDetailsModal(true);
  };

  // Search logic - search in English names
  const filteredCategory = useMemo(() => {
    return categoryData.filter((category) =>
      getEnglishName(category).toLowerCase().includes(searchQuery.toLowerCase())
    );
  }, [categoryData, searchQuery]);

  // Pagination logic
  const totalPages = Math.ceil(filteredCategory.length / entriesPerPage);
  const paginatedCategory = useMemo(() => {
    const startIndex = (currentPage - 1) * entriesPerPage;
    return filteredCategory.slice(startIndex, startIndex + entriesPerPage);
  }, [filteredCategory, currentPage, entriesPerPage]);

  const handleSearch = (e) => {
    setSearchQuery(e.target.value);
    setCurrentPage(1);
  };

  // Handle delete modal open
  const handleDeleteClick = (category) => {
    setCategoryToDelete(category);
    setIsDeleteModalOpen(true);
  };

  // Handle confirm delete
  const handleConfirmDelete = async () => {
    if (!categoryToDelete) return;

    try {
      setDeleteLoading(true);
      await axios.delete(
        `${process.env.REACT_APP_API_URL}/categories/delete-category/${categoryToDelete._id}`
      );
      setIsDeleteModalOpen(false);
      setCategoryToDelete(null);
      await fetchCategories();
    } catch (error) {
      console.error("Error deleting category:", error);
    } finally {
      setDeleteLoading(false);
    }
  };

  const handleCancelDelete = () => {
    setIsDeleteModalOpen(false);
    setCategoryToDelete(null);
  };

  const handlePageChange = (page) => {
    if (page >= 1 && page <= totalPages) setCurrentPage(page);
  };

  const handleUpdateModal = (id) => {
    setCategoryId(id);
    setIsUpdateModal(true);
  };

  return (
    <>
      <section className="min-h-screen">
        {/* Header (conditionally rendered if not inside tab wrapper) */}
        {!hideHeader && (
          <div className="flex justify-between items-center mb-6">
            <h1 className="text-xl md:text-3xl font-philosopher flex items-center gap-2">
              <BaggageClaim className="w-7 h-7 text-blue-600" />
              All Categories
            </h1>
          </div>
        )}

        {/* Main content */}
        <div className="bg-white rounded-lg shadow-sm p-4 sm:p-6 mb-6">
          {/* Search + Add */}
          <div className="flex flex-col md:flex-row justify-between items-center gap-4">
            <div className="relative w-full md:w-auto">
              <input
                value={searchQuery}
                onChange={handleSearch}
                className="border border-gray-300 rounded-lg pl-4 pr-10 py-2 w-full md:w-80 focus:outline-none focus:ring-1 focus:ring-blue-500 text-sm"
                type="text"
                placeholder="Search Categories..."
              />
              <button className="text-xl hover:text-[#308bfc] transition-colors duration-500">
                <Search className="absolute right-3 top-2.5 w-5 h-5 text-gray-400" />
              </button>
            </div>

            <AddOneButton
              onClick={() => setIsAddModal(true)}
              label="Add New Category"
            />
          </div>
        </div>

        {/* Categories grid */}
        <div className="my-5 min-h-[200px]">
          {loading ? (
            <AdminCategorySkeleton />
          ) : error ? (
            <div className="text-center text-red-500">{error}</div>
          ) : filteredCategory.length > 0 ? (
            <div className="grid xl:grid-cols-4 lg:grid-cols-3 md:grid-cols-2 gap-6">
              {paginatedCategory.map((category, index) => {
                const subCount = Array.isArray(category.subcategories) ? category.subcategories.length : 0;
                return (
                  <div
                    key={category._id || index}
                    className="relative bg-white rounded-2xl shadow-md overflow-hidden border border-gray-200 hover:shadow-xl transition-all duration-300 group flex flex-col justify-between"
                  >
                    {/* Image */}
                    <div className="relative w-full h-48 overflow-hidden bg-gray-100">
                      <img
                        src={category.imgUrl || "https://images.unsplash.com/photo-1600585154340-be6161a56a0c?w=400&auto=format&fit=crop&q=60"}
                        alt={getEnglishName(category)}
                        onError={(e) => {
                          e.target.src = "https://images.unsplash.com/photo-1600585154340-be6161a56a0c?w=400&auto=format&fit=crop&q=60";
                        }}
                        className="w-full h-full object-cover transform group-hover:scale-105 transition-transform duration-500"
                      />
                      <div className="absolute inset-0 bg-black/30 opacity-0 group-hover:opacity-100 transition-opacity duration-300 flex items-center justify-center gap-3">
                        <button
                          onClick={() => handleDetailsModal(category)}
                          className="p-2.5 bg-white rounded-full text-[#ea8d2a] hover:scale-110 shadow-md transition-transform"
                          title="View Details"
                        >
                          {icons.eyeIcon}
                        </button>
                        <button
                          onClick={() => handleUpdateModal(category?._id)}
                          className="p-2.5 bg-white rounded-full text-[#6278e8] hover:scale-110 shadow-md transition-transform"
                          title="Edit"
                        >
                          {icons.editIcon}
                        </button>
                        <button
                          onClick={() => handleDeleteClick(category)}
                          className="p-2.5 bg-white rounded-full text-red-500 hover:scale-110 shadow-md transition-transform"
                          title="Delete"
                        >
                          {icons.deleteIcon}
                        </button>
                      </div>
                    </div>

                    {/* Content */}
                    <div className="p-4 flex-1 flex flex-col justify-between">
                      <div>
                        <div className="flex items-start justify-between gap-2">
                          <h3 className="text-base font-bold text-gray-800 truncate capitalize">
                            {getEnglishName(category)}
                          </h3>
                        </div>
                        <p className="text-gray-500 text-xs mt-1.5 line-clamp-2">
                          {getEnglishDescription(category)}
                        </p>
                      </div>

                      {/* Subcategory Pill Button */}
                      <div className="mt-4 pt-3 border-t border-gray-100 flex items-center justify-between">
                        <button
                          type="button"
                          onClick={() => onSelectSubcategoriesOf && onSelectSubcategoriesOf(category._id)}
                          className="inline-flex items-center gap-1.5 text-xs font-semibold px-2.5 py-1 rounded-full bg-blue-50 text-blue-700 hover:bg-blue-100 border border-blue-200 transition"
                          title="Click to view subcategories of this category"
                        >
                          <span>{subCount} Subcategories</span>
                          <span className="text-[10px] text-blue-500 font-bold">→</span>
                        </button>
                        <span className="text-[11px] text-gray-400 font-mono">
                          Seq: {category.sequence || 0}
                        </span>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          ) : (
            <div className="flex flex-col items-center justify-center py-16">
              <div className="text-6xl text-gray-300 mb-4">
                {icons.folderIcon}
              </div>
              <h2 className="text-2xl font-semibold text-gray-500 mb-2">
                No Categories Found
              </h2>
              <p className="text-gray-400 mb-6">
                Get started by creating a new category
              </p>
              <AddOneButton
                onClick={() => setIsAddModal(true)}
                label="Add New Category"
              />
            </div>
          )}
        </div>

        {/* Pagination */}
        {filteredCategory.length > 0 && !loading && (
          <div className="px-6 py-4 border-t border-gray-200 flex flex-col md:flex-row items-center justify-between gap-3">
            <h6 className="text-sm text-gray-500">
              Showing {paginatedCategory.length} of {filteredCategory.length}{" "}
              entries
            </h6>

            <div className="flex items-center gap-1">
              {/* Previous Button */}
              <button
                onClick={() => handlePageChange(currentPage - 1)}
                disabled={currentPage === 1}
                className="p-2 rounded-lg border border-gray-300 hover:bg-gray-100 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
              >
                {icons.leftAngleIcon}
              </button>

              {/* Page Numbers */}
              {Array.from({ length: totalPages }, (_, i) => (
                <button
                  key={i}
                  onClick={() => handlePageChange(i + 1)}
                  className={`px-4 py-2 rounded-lg text-sm font-medium transition-all duration-200
                        ${
                          currentPage === i + 1
                            ? "bg-blue-500 text-white shadow-md"
                            : "border border-gray-300 text-gray-700 hover:bg-gray-100"
                        }`}
                >
                  {i + 1}
                </button>
              ))}

              {/* Next Button */}
              <button
                onClick={() => handlePageChange(currentPage + 1)}
                disabled={currentPage === totalPages}
                className="p-2 rounded-lg border border-gray-300 hover:bg-gray-100 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
              >
                {icons.rightAngleIcon}
              </button>
            </div>
          </div>
        )}
      </section>

      {/* Modals */}
      {isAddModal &&
        createPortal(
          <AddNewModal
            isAddModal={isAddModal}
            onClose={() => setIsAddModal(false)}
            onSuccess={handleSuccess}
          />,
          document.body
        )}

      {isUpdateModal &&
        createPortal(
          <UpdateModal
            onClose={() => setIsUpdateModal(false)}
            onSuccess={handleSuccess}
            isUpdateModal={isUpdateModal}
            categoryId={categoryId}
          />,
          document.body
        )}

      {isDeleteModalOpen &&
        createPortal(
          <DeleteAlertModal
            isOpen={isDeleteModalOpen}
            onClose={handleCancelDelete}
            dltType="category"
            dltName={getEnglishName(categoryToDelete)}
            onConfirm={handleConfirmDelete}
            dltLoading={deleteLoading}
          />,
          document.body
        )}

      {isDetailsModal &&
        createPortal(
          <CategoryDetailsModal
            isOpen={isDetailsModal}
            onClose={() => setIsDetailsModal(false)}
            category={selectedCategory}
          />,
          document.body
        )}
    </>
  );
};

export default AdminCategoryHome;